import { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import DataTable from 'react-data-table-component'
import moment from 'moment'
import { Link, useNavigate } from 'react-router-dom'
import { allBadges, changeBadgeStatus, deleteBadges } from '../../slice/badgeSlice'
import { useModuleSettings } from '../../context/adminSettingsContext'
import { hasPermission } from '../../common/permissions'
import Swal from 'sweetalert2'

const BadgeList = () => {
    const dispatch = useDispatch()
    const { badgeList } = useSelector(state => state.badge);
    const navigate = useNavigate();

    const {
        page,
        setPage,
        limit,
        setLimit,
        filter: statusFilter,
        setFilter: setStatusFilter,
        search: searchQuery,
        setSearch: setSearchQuery,
    } = useModuleSettings('badges', { filter: 'all' })

    const [searchDebounce, setSearchDebounce] = useState(searchQuery)
    const isMounted = useRef(false)

    // Debounce search input
    useEffect(() => {
        if (!isMounted.current) {
            isMounted.current = true
            setSearchDebounce(searchQuery)
            return
        }
        const timer = setTimeout(() => {
            setSearchDebounce(searchQuery)
            setPage(1)
        }, 500)

        return () => clearTimeout(timer)
    }, [searchQuery])

    useEffect(() => {
        dispatch(allBadges({
            page,
            limit,
            filter: statusFilter !== 'all' ? statusFilter : undefined,
            search: searchDebounce || undefined
        }))
    }, [dispatch, page, limit, statusFilter, searchDebounce])

    // Toggle status handler
    const handleToggleStatus = async (badgeId) => {
        try {
            const response = await dispatch(changeBadgeStatus({
                id: badgeId
            }))
            if (response?.payload?.code == 200) {
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    backdrop: false,
                    title: response?.payload?.message,
                    showConfirmButton: false,
                    timer: 1500,
                })
            }
            dispatch(allBadges({
                page,
                limit,
                filter: statusFilter !== 'all' ? statusFilter : undefined,
                search: searchDebounce || undefined
            }))
        } catch (error) {
            console.error('Error toggling badge status:', error)
        }
    }

    // Delete handler
    const handleDelete = async (badgeId) => {
        const result = await Swal.fire({
            title: 'Delete Badge?',
            text: 'This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, delete',
            cancelButtonText: 'Cancel',
            reverseButtons: true,
        })

        if (!result.isConfirmed) return

        try {
            const deleteResponse = await dispatch(deleteBadges({
                id: badgeId
            }))

            if (deleteResponse?.payload?.code == 200) {
                Swal.fire({
                    icon: 'success',
                    title: 'Deleted',
                    text: deleteResponse?.payload?.message,
                    timer: 1500,
                    showConfirmButton: false,
                })
            }

            dispatch(
                allBadges({
                    page,
                    limit,
                    filter: statusFilter !== 'all' ? statusFilter : undefined,
                    search: searchDebounce || undefined
                })
            )
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Failed',
                text: error || 'Failed to delete badge.',
            })
        }
    }

    const handleView = (badgeId) => {
        navigate('/badges/view', {
            state: { badgeId }
        })
    }

    const handleEdit = (badgeId) => {
        navigate('/badges/edit', {
            state: { badgeId }
        })
    }

    const columns = [
        {
            name: 'Sr No',
            width: '80px',
            selector: (_, index) =>
                ((badgeList?.paging?.page_no || 1) - 1) * limit + index + 1,
        },
        {
            name: 'Badge ID',
            selector: row => row.id,
            sortable: true,
            width: '100px',
        },
        {
            name: 'Image',
            width: '100px',
            center: true,
            cell: row => (
                <img
                    src={row.image || process.env.PUBLIC_URL + '/assets/images/default-badge.png'}
                    alt={row.name}
                    onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = process.env.PUBLIC_URL + '/assets/images/default-badge.png';
                    }}
                    style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '8px',
                        objectFit: 'contain',
                        border: '1px solid #dee2e6',
                        padding: '4px',
                        backgroundColor: '#fff'
                    }}
                />
            ),
        },
        {
            name: 'Badge Name',
            selector: row => row.name || 'NA',
            sortable: true,
            minWidth: '160px',
        },
        {
            name: 'Min Bookings',
            selector: row => row.min_completed_bookings ?? 'NA',
            sortable: true,
            width: '130px',
            center: true,
        },
        {
            name: 'Min Rating',
            selector: row => row.min_avg_rating ?? 'NA',
            sortable: true,
            width: '110px',
            center: true,
            cell: row => (
                <span>
                    {row.min_avg_rating ? (
                        <>
                            {row.min_avg_rating}
                            <i className="fe-star" style={{ fontSize: '12px', color: '#ffc107', marginLeft: '4px' }} />
                        </>
                    ) : 'NA'}
                </span>
            ),
        },
        {
            name: 'Hours/Month',
            selector: row => row.min_hours_per_month ?? 'NA',
            sortable: true,
            width: '130px',
            center: true,
            cell: row => row.min_hours_per_month ? `${row.min_hours_per_month}h` : 'NA',
        },
        {
            name: 'Created Date',
            selector: row =>
                row.createdAt
                    ? moment(row.createdAt).local().format('DD MMM YYYY, hh:mm A')
                    : 'NA',
            sortable: true,
            minWidth: '200px',
        },
        {
            name: 'Status',
            width: '100px',
            center: true,
            cell: row => (
                <div className="form-check form-switch">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id={`switch-${row.id}`}
                        checked={row.is_active === 1}
                        onChange={() => handleToggleStatus(row.id)}
                        disabled={!hasPermission('badges', 'edit') || row.is_deleted === 1}
                        style={{
                            cursor: hasPermission('badges', 'edit') && row.is_deleted !== 1 ? 'pointer' : 'not-allowed',
                            opacity: row.is_deleted === 1 ? 0.5 : 1
                        }}
                    />
                </div>
            ),
        },
        {
            name: 'Action',
            width: '120px',
            center: true,
            cell: row => (
                <div className="d-flex gap-2 justify-content-center">
                    {hasPermission('badges', 'view') && (
                        <button
                            className="btn btn-sm btn-outline-primary"
                            title="View"
                            onClick={() => handleView(row.id)}
                            style={{
                                width: '32px',
                                height: '32px',
                                padding: '0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px solid #dee2e6',
                                borderRadius: '4px'
                            }}
                        >
                            <i className="fe-eye" style={{ fontSize: '14px' }} />
                        </button>
                    )}

                    {hasPermission('badges', 'edit') && (
                        <button
                            className="btn btn-sm btn-outline-warning"
                            title="Edit"
                            onClick={() => handleEdit(row.id)}
                            style={{
                                width: '32px',
                                height: '32px',
                                padding: '0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px solid #dee2e6',
                                borderRadius: '4px'
                            }}
                        >
                            <i className="fe-edit" style={{ fontSize: '14px' }} />
                        </button>
                    )}

                    {hasPermission('badges', 'delete') && row.is_deleted !== 1 && (
                        <button
                            className="btn btn-sm btn-outline-danger"
                            title="Delete"
                            onClick={() => handleDelete(row.id)}
                            style={{
                                width: '32px',
                                height: '32px',
                                padding: '0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px solid #dc3545',
                                borderRadius: '4px'
                            }}
                        >
                            <i className="fe-trash-2" style={{ fontSize: '14px' }} />
                        </button>
                    )}
                </div>
            ),
        },
    ]

    const customStyles = {
        table: {
            style: {
                backgroundColor: '#ffffff',
                borderRadius: '8px',
            },
        },
        headRow: {
            style: {
                backgroundColor: '#f8f9fa',
                borderBottom: '2px solid #dee2e6',
                minHeight: '48px',
            },
        },
        headCells: {
            style: {
                fontSize: '13px',
                fontWeight: '600',
                color: '#495057',
                paddingLeft: '12px',
                paddingRight: '12px',
            },
        },
        cells: {
            style: {
                fontSize: '13px',
                color: '#212529',
                paddingTop: '12px',
                paddingBottom: '12px',
                paddingLeft: '12px',
                paddingRight: '12px',
            },
        },
        rows: {
            style: {
                minHeight: '60px',
                '&:hover': {
                    backgroundColor: '#f8f9fa',
                    cursor: 'pointer',
                },
            },
            stripedStyle: {
                backgroundColor: '#ffffff',
            },
        },
        pagination: {
            style: {
                borderTop: '1px solid #dee2e6',
                minHeight: '56px',
                fontSize: '13px',
            },
        },
    }

    return (
        <div className="container-fluid px-4 py-3">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0 fw-semibold">Badges</h4>

                {hasPermission('badges', 'add') && (
                    <Link
                        to="/badges/add"
                        className="btn btn-success"
                        style={{
                            padding: '8px 20px',
                            fontSize: '14px',
                            fontWeight: '500',
                            borderRadius: '6px'
                        }}
                    >
                        + Add Badge
                    </Link>
                )}
            </div>

            {/* Search and Filter */}
            <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: '8px' }}>
                <div className="card-body p-3">
                    <div className="row g-3">
                        <div className="col-md-8">
                            <div className="position-relative">
                                <i
                                    className="fe-search position-absolute"
                                    style={{
                                        left: '12px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: '#6c757d',
                                        fontSize: '16px'
                                    }}
                                />
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by badge name or description..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    style={{
                                        paddingLeft: '38px',
                                        paddingRight: searchQuery ? '38px' : '12px',
                                        height: '40px',
                                        fontSize: '14px',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '6px'
                                    }}
                                />
                                {searchQuery && (
                                    <button
                                        className="btn btn-link position-absolute p-0"
                                        type="button"
                                        onClick={() => setSearchQuery('')}
                                        style={{
                                            right: '12px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            color: '#6c757d'
                                        }}
                                    >
                                        <i className="fe-x" style={{ fontSize: '16px' }}></i>
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="col-md-4">
                            <select
                                className="form-select"
                                value={statusFilter}
                                onChange={(e) => {
                                    setStatusFilter(e.target.value)
                                    setPage(1)
                                }}
                                style={{
                                    height: '40px',
                                    fontSize: '14px',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '6px'
                                }}
                            >
                                <option value="all">All</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                                <option value="deleted">Deleted</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                <DataTable
                    columns={columns}
                    data={badgeList?.data || []}
                    pagination
                    paginationServer
                    paginationTotalRows={badgeList?.paging?.total || 0}
                    paginationPerPage={limit}
                    paginationDefaultPage={badgeList?.paging?.page_no || 1}
                    onChangeRowsPerPage={(newLimit) => {
                        setLimit(newLimit)
                        setPage(1)
                    }}
                    onChangePage={(newPage) => setPage(newPage)}
                    responsive
                    highlightOnHover
                    persistTableHead
                    progressPending={badgeList?.loading}
                    noDataComponent={
                        <div className="p-5 text-center">
                            <i className="fe-award" style={{ fontSize: '48px', color: '#adb5bd' }}></i>
                            <h6 className="mt-3 mb-1 text-muted">
                                {searchQuery ? 'No badges found matching your search' : 'No badges found'}
                            </h6>
                            <p className="text-muted small mb-0">
                                {searchQuery ? 'Try adjusting your search criteria' : 'Get started by adding a new badge'}
                            </p>
                        </div>
                    }
                    customStyles={customStyles}
                />
            </div>
        </div>
    )
}

export default BadgeList;